import { Injectable } from '@angular/core';
import {
  defer,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FeathersService } from './feathers.service';
import { ApiEndpoint } from '../../enums';
import { CertificateCreateRequest } from '../../models/certificate-create-request';
import {
  Certificate,
  CertificateMeasurementUnit,
} from '../../models/certificate';
import { ApiSearchResponse } from '../../models/api-search-response';
import { Edition } from '../../models/edition';
import { DialogService, ProgressDialogData } from '../dialog.service';
import { handlePluralTranslation } from '../../utils';
import { SnackBarService } from '../snack-bar.service';
import { DialogParams } from '../../models/dialog-params';
import { EditionCreateRequest } from '../../models/edition-create-request';
import { ReleaseCertificateRequest } from '../../models/release-certificate-request';
import { GlobalLoadingBarService } from '../global-loading-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { CertificateHistory } from '../../models/certificate-history';
import { Router } from '@angular/router';
import { TagsService } from './tags.service';

@Injectable({
  providedIn: 'root',
})
export class CertificatesService {
  constructor(
    private feathers: FeathersService,
    private dialog: DialogService,
    private message: SnackBarService,
    private loading: GlobalLoadingBarService,
    private router: Router,
    private translateService: TranslateService,
    private tagsService: TagsService
  ) {}

  // TODO check argument types
  createCertificates(
    certificates:
      | CertificateCreateRequest
      | CertificateCreateRequest[]
      | Certificate
  ): Observable<Certificate | Certificate[]> {
    return this.feathers
      .post<Certificate>(ApiEndpoint.Certificates, certificates)
      .pipe(
        switchMap((res) =>
          res.tagId
            ? this.tagsService.pendingTagId$.pipe(
                take(1),
                tap((pendingTagId) => {
                  if (!pendingTagId) {
                    return;
                  }
                  const normalizedRes = Array.isArray(res) ? res : [res];
                  const certWithPendingTag = normalizedRes.find(
                    (cert) => cert.tagId && cert.tagId === pendingTagId
                  );
                  if (certWithPendingTag) {
                    this.tagsService.clearPendingTagId();
                    this.message.success(
                      this.translateService.instant(
                        'snackbar.success.pendingTagIdAssociated'
                      )
                    );
                  }
                }),
                map(() => res)
              )
            : of(res)
        )
      );
  }

  updateCertificate(
    id: string,
    updatedCertificate: Certificate
  ): Observable<Certificate> {
    return this.feathers
      .patch(ApiEndpoint.Certificates, id, updatedCertificate)
      .pipe(
        switchMap((res) =>
          res.tagId
            ? this.tagsService.pendingTagId$.pipe(
                take(1),
                tap((pendingTagId) => {
                  if (pendingTagId && pendingTagId === res.tagId) {
                    this.tagsService.clearPendingTagId();
                    this.message.success(
                      this.translateService.instant(
                        'snackbar.success.pendingTagIdAssociated'
                      )
                    );
                  }
                }),
                map(() => res)
              )
            : of(res)
        )
      );
  }

  updateEdition(
    id: string,
    updatedEdition: EditionCreateRequest
  ): Observable<Edition> {
    return this.feathers.patch(ApiEndpoint.Editions, id, updatedEdition);
  }

  watchCertificate(
    id: string,
    skipDefaultErrorHandler = false
  ): Observable<Certificate> {
    return this.feathers
      .watchItem<Certificate>(
        ApiEndpoint.Certificates,
        id,
        !skipDefaultErrorHandler
      )
      .pipe(map((item) => new Certificate(item)));
  }

  watchEdition(
    id: string,
    skipDefaultErrorHandler = false
  ): Observable<Edition> {
    return this.feathers
      .watchItem<Edition>(ApiEndpoint.Editions, id, !skipDefaultErrorHandler)
      .pipe(map((item) => new Edition(item)));
  }

  watchAllCertificates(
    query?: any,
    matcher?: (
      query: any
    ) => (item: any, index: number, array: any[]) => boolean
  ): Observable<ApiSearchResponse<Certificate>> {
    if (!query) {
      query = {};
    }
    if (!query?.$limit) {
      query.$limit = 1000;
    }
    return this.feathers
      .watchSearch<Certificate>(
        ApiEndpoint.Certificates,
        { query },
        true,
        matcher
      )
      .pipe(
        map((res) => {
          res.data = res.data.map(
            (certificate) => new Certificate(certificate)
          );
          return new ApiSearchResponse<Certificate>(res);
        })
      );
  }

  getCertificatesListsUnits(
    query?: any
  ): Observable<ApiSearchResponse<CertificateMeasurementUnit>> {
    return this.feathers.watchSearch<CertificateMeasurementUnit>(
      ApiEndpoint.CertificatesListsUnits,
      query
    );
  }

  parseCertificate(payload: any): Observable<Certificate[]> {
    return this.feathers.post(ApiEndpoint.CertificatesParse, payload);
  }

  createEdition(edition: EditionCreateRequest): Observable<any> {
    return this.feathers.post(ApiEndpoint.Editions, edition);
  }

  watchAllEditions = (
    query?: any,
    matcher?: (
      query: any
    ) => (item: any, index: number, array: any[]) => boolean
  ): Observable<ApiSearchResponse<Edition>> => {
    if (!query) {
      query = {};
    }
    return this.feathers
      .watchSearch<Edition>(ApiEndpoint.Editions, { query }, true, matcher)
      .pipe(
        map((res) => {
          res.data = res.data.map((edition) => new Edition(edition));
          return new ApiSearchResponse<Edition>(res);
        })
      );
  };

  deleteCertificate(certificate: Certificate): Observable<Certificate> {
    return this.feathers.delete<Certificate>(
      ApiEndpoint.Certificates,
      certificate._id!
    );
  }

  deleteEdition(edition: Edition): Observable<Edition> {
    return this.feathers.delete<Edition>(ApiEndpoint.Editions, edition._id!);
  }

  cloneCertificates(
    certificates: Certificate | Certificate[]
  ): Observable<Certificate | Certificate[]> {
    let requests: CertificateCreateRequest | CertificateCreateRequest[] = [];
    if (Array.isArray(certificates)) {
      requests = certificates.map(
        (cert) => new CertificateCreateRequest(new Certificate(cert))
      );
    } else if (certificates) {
      requests = new CertificateCreateRequest(new Certificate(certificates));
    }
    return this.createCertificates(requests);
  }

  mintCertificate(certificate: Certificate): Observable<Certificate> {
    return this.feathers.post<Certificate>(ApiEndpoint.CertificatesMint, {
      certificate: certificate._id,
    });
  }

  releaseCertificate(
    certificate: Certificate | ReleaseCertificateRequest
  ): Observable<Certificate> {
    const withOptions = !(certificate instanceof Certificate);
    const request = withOptions
      ? {
          certificate: certificate.certificate?._id,
          expirationDate: certificate.expirationDate,
          sendTo: certificate.sendTo,
        }
      : { certificate: certificate._id };
    return this.feathers.post<Certificate>(
      ApiEndpoint.CertificatesRelease,
      request
    );
  }

  cancelCertificateRelease(certificate: Certificate): Observable<Certificate> {
    return this.feathers.delete<Certificate>(
      ApiEndpoint.CertificatesRelease,
      certificate._id!
    );
  }

  getHandledFlow(settings: {
    certificates: Certificate | Certificate[];
    call?: (certificate: Certificate) => Observable<Certificate>;
    arrayCall?: (
      certificates: Certificate | Certificate[]
    ) => Observable<Certificate | Certificate[]>;
    singleSuccessText: (certificate: Certificate) => string;
    multipleSuccessText: (certificates: Certificate[]) => string;
    dialogSettings: (
      amount: number,
      certificate: Certificate | null,
      certificates: Certificate[] | null
    ) => DialogParams;
    progressDialogSettings?: Omit<ProgressDialogData, 'call' | 'isMultiple'>;
  }): Observable<Certificate | Certificate[] | false> {
    if (!settings.call && !settings.arrayCall) {
      return of(false);
    }
    const amount = Array.isArray(settings.certificates)
      ? settings.certificates.length
      : 1;
    const certificate =
      amount === 1 ? (settings.certificates as Certificate) : null;
    const certArray =
      amount > 1 ? (settings.certificates as Certificate[]) : null;
    return this.dialog
      .open(settings.dialogSettings(amount, certificate, certArray))
      .result.pipe(
        switchMap((result: boolean | any) => {
          if (!result) {
            return of(result);
          }

          const resultAsArgument =
            typeof result === 'boolean' ? undefined : result;

          let usedCall;
          let isMultiple = false;

          if (Array.isArray(certArray)) {
            isMultiple = true;
            if (settings.arrayCall) {
              usedCall = settings.arrayCall.bind(this)(
                resultAsArgument ?? certArray
              );
            } else {
              usedCall = forkJoin<Certificate[]>(
                (resultAsArgument ?? certArray).map((cert: any) =>
                  settings.call!.bind(this)(cert)
                )
              );
            }
            usedCall = usedCall.pipe(
              tap((res) => {
                const normalizedRes = Array.isArray(res) ? res : [res];
                this.message.success(
                  settings.multipleSuccessText(normalizedRes)
                );
              })
            );
          } else if (certificate) {
            isMultiple = false;
            if (settings.arrayCall) {
              usedCall = settings.arrayCall.bind(this)(
                resultAsArgument ?? certificate
              );
            } else {
              usedCall = settings.call!.bind(this)(
                resultAsArgument ?? certificate
              );
            }
            usedCall = usedCall.pipe(
              tap((res) => {
                const normalizedRes = Array.isArray(res) ? res[0] : res;
                this.message.success(settings.singleSuccessText(normalizedRes));
              })
            );
          }

          if (!usedCall) {
            return of<false>(false);
          }

          if (!settings.progressDialogSettings) {
            return usedCall;
          }

          return this.dialog.openProgressDialog({
            ...settings.progressDialogSettings,
            call: usedCall,
            isMultiple,
          }).result;
        })
      );
  }

  cloneCertificatesHandled = (
    certificates: Certificate | Certificate[]
  ): Observable<Certificate | Certificate[] | false> => {
    return this.dialog.openCertificateCloneDialog(
      Array.isArray(certificates) ? certificates : [certificates]
    );
  };

  deleteCertificatesHandled = (
    certificates: Certificate | Certificate[]
  ): Observable<Certificate | Certificate[] | false> => {
    return this.getHandledFlow({
      certificates: certificates,
      call: this.deleteCertificate,
      singleSuccessText: (certificate) =>
        this.translateService.instant(
          'snackbar.success.deleteCertificateSingle',
          {
            certificateName: certificate.name,
          }
        ),
      multipleSuccessText: (certificates) =>
        this.translateService.instant(
          'snackbar.success.deleteCertificateMultiple',
          {
            amount: certificates.length,
          }
        ),
      dialogSettings: (
        amount: number,
        singleCertificate?: Certificate | null
      ) => ({
        title:
          amount === 1
            ? this.translateService.instant(
                'dialogs.deleteCertificate.titleSingle'
              )
            : this.translateService.instant(
                'dialogs.deleteCertificate.titleMultiple',
                {
                  amount,
                  certificateForm: this.translateService.instant(
                    handlePluralTranslation(amount, 'certificate')
                  ),
                }
              ),
        text: singleCertificate
          ? this.translateService.instant(
              'dialogs.deleteCertificate.textSingle',
              { certificateName: singleCertificate.name }
            )
          : this.translateService.instant(
              'dialogs.deleteCertificate.textMultiple',
              { amount }
            ),
        confirmButtonText: singleCertificate
          ? this.translateService.instant(
              'dialogs.deleteCertificate.confirmSingle'
            )
          : this.translateService.instant(
              'dialogs.deleteCertificate.confirmMultiple'
            ),
      }),
    });
  };

  deleteEditionHandled = (edition: Edition): Observable<Edition | false> => {
    const getDeleteEditionDescriptionText = (edition: Edition) => {
      if (edition.containsClaimed) {
        return this.translateService.instant(
          'dialogs.deleteEdition.textClaimed'
        );
      } else {
        if (edition.total) {
          return this.translateService.instant(
            'dialogs.deleteEdition.textNonEmpty',
            {
              amount: edition.total,
              certificateForm: this.translateService.instant(
                handlePluralTranslation(edition.total, 'certificate')
              ),
              editionName: edition.name,
            }
          );
        }
        return this.translateService.instant(
          'dialogs.deleteEdition.textEmpty',
          { editionName: edition.name }
        );
      }
    };

    return this.dialog
      .open({
        title: this.translateService.instant('dialogs.deleteEdition.title', {
          editionName: edition.name,
        }),
        text: getDeleteEditionDescriptionText(edition),
        confirmButtonType: 'warn',
        confirmButtonText: edition.containsClaimed
          ? this.translateService.instant(
              'dialogs.deleteEdition.confirmUnclaimedDelete'
            )
          : this.translateService.instant(
              'dialogs.deleteEdition.confirmEditionDelete',
              { editionName: edition.name }
            ),
      })
      .result.pipe(
        switchMap((res) => {
          if (!res) {
            return of(res);
          }
          return this.deleteEdition(edition).pipe(
            tap((deletedEdition) => {
              this.message.success(
                edition.containsClaimed
                  ? this.translateService.instant(
                      'snackbar.success.deleteEditionUnclaimed',
                      { editionName: deletedEdition.name }
                    )
                  : this.translateService.instant(
                      'snackbar.success.deleteEdition',
                      {
                        editionName: deletedEdition.name,
                      }
                    )
              );
            })
          );
        })
      );
  };

  mintCertificatesHandled = (
    certificates: Certificate | Certificate[]
  ): Observable<Certificate | Certificate[] | false> => {
    const call = defer(() =>
      this.getHandledFlow({
        certificates: certificates,
        call: this.mintCertificate,
        multipleSuccessText: (res) =>
          this.translateService.instant(
            'snackbar.success.mintCertificateMultiple',
            {
              amount: res.length,
            }
          ),
        singleSuccessText: (res) =>
          this.translateService.instant(
            'snackbar.success.mintCertificateSingle',
            {
              certificateName: res.name ?? res.general?.productTitle ?? '',
            }
          ),
        dialogSettings: (amount: number) => ({
          price: {
            for: this.translateService.instant(
              'dialogs.mintCertificate.priceFor'
            ),
            amount: this.feathers.settingsObject$.value?.mintPrice!,
            explicit: amount > 1,
          },
          title:
            amount === 1
              ? this.translateService.instant(
                  'dialogs.mintCertificate.titleSingle'
                )
              : this.translateService.instant(
                  'dialogs.mintCertificate.titleMultiple',
                  {
                    amount,
                    certificateForm: this.translateService.instant(
                      handlePluralTranslation(amount, 'certificate')
                    ),
                  }
                ),
          text:
            amount === 1
              ? this.translateService.instant(
                  'dialogs.mintCertificate.textSingle'
                )
              : this.translateService.instant(
                  'dialogs.mintCertificate.textMultiple'
                ),
          confirmButtonText:
            amount === 1
              ? this.translateService.instant(
                  'dialogs.mintCertificate.confirmSingle',
                  {
                    certificateForm: this.translateService.instant(
                      handlePluralTranslation(amount, 'certificate')
                    ),
                  }
                )
              : this.translateService.instant(
                  'dialogs.mintCertificate.confirmMultiple',
                  {
                    certificateForm: this.translateService.instant(
                      handlePluralTranslation(amount, 'certificate')
                    ),
                  }
                ),
        }),
        progressDialogSettings: {
          text: {
            single: {
              loading: this.translateService.instant(
                'dialogs.mintCertificate.progressDialog.single.loading'
              ),
              success: this.translateService.instant(
                'dialogs.mintCertificate.progressDialog.single.success'
              ),
              failure: this.translateService.instant(
                'dialogs.mintCertificate.progressDialog.single.failure'
              ),
            },
            multiple: {
              loading: this.translateService.instant(
                'dialogs.mintCertificate.progressDialog.multiple.loading'
              ),
              success: this.translateService.instant(
                'dialogs.mintCertificate.progressDialog.multiple.success'
              ),
              failure: this.translateService.instant(
                'dialogs.mintCertificate.progressDialog.multiple.failure'
              ),
            },
          },
          delay: 1000,
        },
      })
    );

    return this.checkPrice('mint', certificates, call);
  };

  checkPrice(
    action: 'mint' | 'release',
    certificates: Certificate | Certificate[],
    call: Observable<false | Certificate | Certificate[]>
  ) {
    return defer(() =>
      this.feathers.me$.pipe(
        take(1),
        switchMap((user) => {
          const certArray = Array.isArray(certificates)
            ? certificates
            : [certificates];
          const userCredits = user!.credits ?? 0;
          const mintPrice = this.feathers.settingsObject$.value?.mintPrice ?? 0;
          const priceTotal = certArray.length * mintPrice;
          const isEnoughCredits = userCredits >= priceTotal;

          if (isEnoughCredits) {
            return call;
          } else {
            const textInterpolation = {
              amount: certArray.length,
              certificateForm: this.translateService.instant(
                handlePluralTranslation(certArray.length, 'certificate')
              ),
              creditsTotalPrice: priceTotal,
              creditForm: this.translateService.instant(
                handlePluralTranslation(userCredits, 'creditName')
              ),
              userCreditsAmount: userCredits,
            };

            return defer(
              () =>
                this.dialog.open({
                  title: this.translateService.instant(
                    `dialogs.notEnoughCredits.${action}.title`,
                    {
                      creditNamePlural:
                        this.translateService.instant('creditNamePlural'),
                    }
                  ),
                  text: this.translateService.instant(
                    `dialogs.notEnoughCredits.${action}.text`,
                    textInterpolation
                  ),
                  confirmButtonText: this.translateService.instant(
                    `dialogs.notEnoughCredits.${action}.confirmButtonText`,
                    {
                      creditNamePlural:
                        this.translateService.instant('creditNamePlural'),
                    }
                  ),
                  price: {
                    for: this.translateService.instant(
                      `dialogs.notEnoughCredits.${action}.${action}`
                    ),
                    amount: mintPrice,
                    explicit: true,
                  },
                }).result
            ).pipe(
              switchMap((result) => {
                if (result) {
                  this.router.navigate(['profile', 'credits']).then();
                }
                return of(false);
              })
            );
          }
        })
      )
    ) as Observable<false | Certificate | Certificate[]>;
  }

  releaseCertificatesHandled = (
    certificates: Certificate | Certificate[]
  ): Observable<Certificate | Certificate[] | false> => {
    const amount = Array.isArray(certificates) ? certificates.length : 1;
    const singleCertificate =
      amount === 1 ? (certificates as Certificate) : null;
    const certArray = amount > 1 ? (certificates as Certificate[]) : null;

    return this.checkPrice(
      'release',
      certificates,
      defer(() =>
        this.dialog.openReleaseDialog({ singleCertificate, certArray })
      )
    );
  };

  cancelReleaseHandled = (
    certificates: Certificate | Certificate[]
  ): Observable<Certificate | Certificate[] | false> => {
    return this.getHandledFlow({
      certificates: certificates,
      call: this.cancelCertificateRelease,
      multipleSuccessText: (res) =>
        this.translateService.instant(
          'snackbar.success.cancelReleaseCertificateMultiple',
          {
            amount: res.length,
          }
        ),
      singleSuccessText: (res) =>
        this.translateService.instant(
          'snackbar.success.cancelReleaseCertificateSingle',
          {
            certificateName: res.name ?? res.general?.productTitle ?? '',
          }
        ),
      dialogSettings: (amount: number, singleCertificate) => ({
        title:
          amount === 1
            ? this.translateService.instant('dialogs.cancelRelease.titleSingle')
            : this.translateService.instant(
                'dialogs.cancelRelease.titleMultiple',
                {
                  amount,
                }
              ),
        text:
          amount === 1
            ? this.translateService.instant(
                'dialogs.cancelRelease.textSingle',
                {
                  certificateName:
                    singleCertificate?.name ??
                    singleCertificate?.general?.productTitle ??
                    '',
                }
              )
            : this.translateService.instant(
                'dialogs.cancelRelease.textMultiple',
                {
                  amount,
                }
              ),
        confirmButtonType: 'warn',
        confirmButtonText:
          amount === 1
            ? this.translateService.instant(
                'dialogs.cancelRelease.confirmSingle'
              )
            : this.translateService.instant(
                'dialogs.cancelRelease.confirmMultiple',
                {
                  amount,
                }
              ),
      }),
    });
  };

  watchPublicCertificatePreview(
    id: string,
    skipDefaultErrorMessageHandler = false
  ): Observable<Certificate> {
    return this.feathers.watchItem<Certificate>(
      ApiEndpoint.CertificatesPreview,
      id,
      !skipDefaultErrorMessageHandler
    );
  }

  getPublicCertificatePreview(
    id: string,
    skipDefaultErrorMessageHandler = false
  ): Observable<Certificate> {
    return this.feathers.get<Certificate>(
      ApiEndpoint.CertificatesPreview,
      id,
      !skipDefaultErrorMessageHandler
    );
  }

  watchCertificateHistory(
    id: string,
    skipDefaultErrorMessageHandler = false
  ): Observable<Required<CertificateHistory>> {
    return this.feathers.watchItem<Required<CertificateHistory>>(
      ApiEndpoint.CertificatesHistory,
      id,
      !skipDefaultErrorMessageHandler
    );
  }
}
